import { Document, parseAllDocuments } from 'yaml';

import { EnvironmentType } from '@/react/portainer/environments/types';

export const DeploymentType = {
  Compose: 'compose',
  Kubernetes: 'kubernetes',
} as const;

export type DeploymentType =
  (typeof DeploymentType)[keyof typeof DeploymentType];

export const DEPLOYMENT_TYPE_LABELS: Record<DeploymentType, string> = {
  [DeploymentType.Compose]: 'Docker Compose',
  [DeploymentType.Kubernetes]: 'Kubernetes',
};

export const DEPLOYMENT_TYPE_ENVIRONMENT_TYPE: Record<
  DeploymentType,
  EnvironmentType
> = {
  [DeploymentType.Compose]: EnvironmentType.EdgeAgentOnDocker,
  [DeploymentType.Kubernetes]: EnvironmentType.EdgeAgentOnKubernetes,
};

export const UNKNOWN_DEPLOYMENT_TYPE = 'unknown' as const;

type DetectedDeploymentType = DeploymentType | typeof UNKNOWN_DEPLOYMENT_TYPE;

const KUBERNETES_API_VERSION_PATTERN = /^apiVersion:/m;
const KUBERNETES_KIND_PATTERN = /^kind:/m;
const COMPOSE_SERVICES_PATTERN = /^services:/m;

export function detectDeploymentType(
  fileContent: string,
  fileName: string
): DetectedDeploymentType {
  const fromContent = detectFromContent(fileContent);

  return fromContent === UNKNOWN_DEPLOYMENT_TYPE
    ? detectFromFileName(fileName)
    : fromContent;
}

function detectFromContent(fileContent: string): DetectedDeploymentType {
  const documents = parseYamlDocuments(fileContent);

  if (documents.length > 0) {
    if (documents.some(isKubernetesManifest)) {
      return DeploymentType.Kubernetes;
    }
    if (documents.some(isComposeManifest)) {
      return DeploymentType.Compose;
    }
    return UNKNOWN_DEPLOYMENT_TYPE;
  }

  // not valid standalone YAML (e.g. a Helm template) - fall back to a text scan
  if (
    KUBERNETES_API_VERSION_PATTERN.test(fileContent) &&
    KUBERNETES_KIND_PATTERN.test(fileContent)
  ) {
    return DeploymentType.Kubernetes;
  }
  if (COMPOSE_SERVICES_PATTERN.test(fileContent)) {
    return DeploymentType.Compose;
  }

  return UNKNOWN_DEPLOYMENT_TYPE;
}

function detectFromFileName(fileName: string): DetectedDeploymentType {
  const filename = fileName.split('/').pop() ?? fileName;

  return filename.includes('compose')
    ? DeploymentType.Compose
    : UNKNOWN_DEPLOYMENT_TYPE;
}

function parseYamlDocuments(content: string): Document.Parsed[] {
  try {
    return parseAllDocuments(content).filter((doc) => doc.errors.length === 0);
  } catch {
    return [];
  }
}

function isKubernetesManifest(doc: Document.Parsed): boolean {
  const value: unknown = doc.toJSON();

  return isPlainObject(value) && 'apiVersion' in value && 'kind' in value;
}

function isComposeManifest(doc: Document.Parsed): boolean {
  const value: unknown = doc.toJSON();

  return isPlainObject(value) && isServiceMap(value.services);
}

// a bare `services` key can appear in unrelated configs, so require the shape too
function isServiceMap(value: unknown): boolean {
  const services = isPlainObject(value) ? Object.values(value) : [];

  return services.length > 0 && services.every(isPlainObject);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
